// Doing sign in with email link then redirect

import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Card from './Card';
import Input from './Input';
import { signInByLink, authCheckAuthByEmail } from '../utils/firebase';
import { storageEmailForSignIn } from '../utils/consts';
import isEmail from 'validator/lib/isEmail';
import './EmailLinkSignInCard.scss';

export default function EmailLinkSignInCard() {
  const [spinner, setSpinner] = useState(false);
  const signInCalled = useRef(false); // We use this to prevent duplicate signIn call
  const navigate = useNavigate();

  // It's signInWithEmailLink wrapper
  const signIn = useCallback(
    async (email) => {
      // Preventing duplicate sign in
      if (signInCalled.current) return;
      signInCalled.current = true;

      try {
        setSpinner(true);

        // User should be registered already
        const checkEmailRes = await authCheckAuthByEmail({ email });
        if (!checkEmailRes?.data) {
          throw new Error(
            'Error: the email is not registered. Use an invitation link to register',
          );
        }

        await signInByLink(email);
        localStorage.removeItem(storageEmailForSignIn);
        navigate('/login', { replace: true });
      } catch (error) {
        // That's because the function is calling twice for some reason and we getting the error
        if (!error?.message?.includes('auth/email-already-in-use')) {
          toast.error(error?.message ?? 'Failed to sign in');
        }
      } finally {
        setSpinner(false);
        signInCalled.current = false;
      }
    },
    [navigate],
  );

  // Don't need to confirm email if we have it local storage
  useEffect(() => {
    const email = localStorage.getItem(storageEmailForSignIn);
    if (email) signIn(email);
  }, [signIn]);

  const initialValues = {
    email: '',
  };

  const validationSchema = new Yup.ObjectSchema({
    email: Yup.string()
      .test('is-valid-email', 'Invalid email', (value) => isEmail(value))
      .required('Required'),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => signIn(values.email),
  });

  return (
    <Card
      header='Confirm email'
      className='email-link-signin-card'
      spinner={spinner}
    >
      <form className='email-link-signin-form' onSubmit={formik.handleSubmit}>
        <p className='font-16'>
          Please confirm your email to finish authentication.
        </p>
        <Input
          name='email'
          placeholder='E-mail'
          {...formik.getFieldProps('email')}
          error={
            formik.touched.email && formik.errors.email
              ? formik.errors.email
              : null
          }
        />
        <button type='submit'>Confirm</button>
      </form>
    </Card>
  );
}
